import * as React from "react";
import { X, Check } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";

export interface FilterBadgeProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display after the checkmark */
  icon?: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  /** Optional className for the icon (e.g. for chart colors) */
  iconClassName?: string;
  /** Content to display (usually the filter label) */
  children: React.ReactNode;
  /** Callback when clear button is clicked */
  onClear?: () => void;
  /** Whether to show the checkmark indicator */
  showCheckmark?: boolean;
  /** Whether the badge is interactive (clickable, shows hover states) */
  interactive?: boolean;
  /** Render as a button element (for use as dropdown trigger) */
  asChild?: boolean;
}

/**
 * FilterBadge - A badge component for displaying active filters
 * Features a checkmark indicator, optional icon, label, and clear button
 *
 * Can be used as:
 * - Static badge with clear button (MonitorDetail style)
 * - Interactive badge as dropdown trigger (TestList style)
 */
export const FilterBadge = React.forwardRef<
  HTMLButtonElement,
  FilterBadgeProps
>(
  (
    {
      icon: Icon,
      iconClassName,
      children,
      onClear,
      className,
      showCheckmark = false,
      interactive = false,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const baseStyles = cn(
      "flex items-center gap-1.5 px-2.5 py-1 rounded-full type-body-xs-regular bg-black/[0.04] text-neutral-900 dark:bg-white/[0.04] dark:text-neutral-200",
      interactive &&
        "hover:bg-black/[0.08] dark:hover:bg-white/[0.08] transition-colors duration-150 cursor-pointer",
      interactive &&
        "outline-none focus-visible:outline-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
      className,
    );

    const content = (
      <>
        {showCheckmark && (
          <div className="w-4 h-4 rounded-full border border-neutral-400 dark:border-neutral-500 flex items-center justify-center flex-shrink-0">
            <Check
              className="w-2 h-2 text-neutral-600 dark:text-neutral-400"
              strokeWidth={3}
            />
          </div>
        )}
        {Icon && (
          <Icon className={cn("w-3 h-3 flex-shrink-0", iconClassName)} />
        )}
        {children}
        {onClear && (
          <div
            role="button"
            tabIndex={0}
            data-clear-filters
            onPointerDown={(e) => {
              // Prevent Radix dropdown from opening when clicking clear
              // Using pointerdown only (not onClick) to avoid double invocation
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }
            }}
            className="cursor-pointer hover:opacity-70"
            aria-label="Clear filter"
          >
            <X className="w-3.5 h-3.5 ml-1 flex-shrink-0" />
          </div>
        )}
      </>
    );

    // Render as button when interactive or asChild
    if (interactive || asChild) {
      return (
        <button ref={ref} className={baseStyles} type="button" {...props}>
          {content}
        </button>
      );
    }

    // Render as div for static badges
    return (
      <div
        className={baseStyles}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        {content}
      </div>
    );
  },
);

FilterBadge.displayName = "FilterBadge";
